import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

/** Apply getRowClassName to a cell, optionally stripping opacity / all row classes. */
function resolveCellRowClassName(column, rowClassName) {
  if (!rowClassName) return "";
  if (column.excludeFromRowClassName) return "";
  if (column.excludeOpacityFromRowClassName) {
    return rowClassName
      .split(/\s+/)
      .filter((cls) => cls && !cls.startsWith("opacity-"))
      .join(" ");
  }
  return rowClassName;
}

const CustomTable = ({
  columns,
  data,
  sortConfig,
  onSort,
  emptyMessage = "No data found",
  emptyDescription = "No records available.",
  isSearchActive = false,
  className = "",
  getRowClassName,
  edgeToEdge = false,
  rowKey = "id",
  /** Clickable row (e.g. navigation); use stopPropagation on action cells */
  onRowClick,
  /** Extra classes on each `<tr>` (e.g. hover) */
  tableRowClassName = "",
  /** Optional per-column header cell padding (e.g. My RFIs grid); default px-3 py-3.5 */
  getHeaderCellPaddingClass,
  /** Optional per-column body cell padding; default px-3 py-4 */
  getBodyCellPaddingClass,
  /** Appended to the scroll wrapper around the table */
  scrollWrapperClassName = "",
  /** Tailwind divide color utility on `<table>` (default divide-gray-300) */
  tableDivideClassName = "divide-gray-300",
  /** Optional `<thead>` className */
  theadClassName,
  /** Optional `<tbody>` className */
  tbodyClassName,
  /**
   * When true, body rows use `transition-colors` only (no border-b); pair with `tbodyClassName`
   * using divide-y (e.g. Innovation Lab grids).
   */
  useDividedBodyRows = false,
  /**
   * When true, while the table is taller than the viewport and has horizontal
   * overflow, a synced scrollbar strip is fixed to the bottom of the screen
   * (aligned to the table width). When you scroll until the real scrollbar is
   * in view, the fixed strip hides and the native horizontal scrollbar is used.
   * When false, only the default overflow-x scrollbar on the table wrapper is used.
   */
  stickyScroll = true,
  /**
   * When true (default), the fixed shell matches the table root (or `stickyMirrorOuterRef`);
   * the invisible scroll mirror stays the width of the scroll viewport for sync, while the
   * visible track spans the full shell so the bar matches the card. Set false to align
   * everything to the scroll container only.
   */
  stickyMirrorBleed = true,
  /** Optional ref to an element whose width/left define the outer strip; used when `stickyMirrorBleed` (defaults to this component’s root). */
  stickyMirrorOuterRef: stickyMirrorOuterRefProp = null,
  /**
   * When true, uses `table-layout: fixed` and mirrors `column.width` on body cells so column
   * widths stay stable when rows change (e.g. sort) instead of growing with content.
   * Optional on each column: `minWidth` (CSS string) for minimum column width; `contentMaxWidth`
   * to cap long text while keeping headers readable. Table uses `w-max min-w-full` so horizontal
   * scroll appears instead of squashing headers.
   */
  fixedColumnWidths = false,
}) => {
  const [tableHeight, setTableHeight] = useState("auto");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);
  const [mirrorWidth, setMirrorWidth] = useState(0);
  /** When the table's horizontal scrollbar sits below the viewport, we show a fixed mirror at the screen bottom. */
  const [viewportMirrorActive, setViewportMirrorActive] = useState(false);
  const tableRef = useRef(null);
  /** Bottom of this rect must not depend on whether the horizontal scrollbar is visible (avoids setState feedback loops). */
  const tableMeasureRef = useRef(null);
  const dropdownObserverRef = useRef(null);
  const scrollContainerRef = useRef(null);
  /** Wrapper positioned fixed; padding lives here so the native scrollbar is inset from the card edges. */
  const mirrorOuterRef = useRef(null);
  const mirrorScrollRef = useRef(null);
  const customTrackRef = useRef(null);
  const customThumbRef = useRef(null);
  const scrollSyncingRef = useRef(false);

  /** DOM-only: position and size of the mirror horizontal custom scrollbar thumb. */
  const updateCustomScrollbarThumb = useCallback(() => {
    const el = mirrorScrollRef.current;
    const track = customTrackRef.current;
    const thumb = customThumbRef.current;
    if (!el || !track || !thumb) return;
    const { scrollWidth, clientWidth } = el;
    const maxScroll = Math.max(0, scrollWidth - clientWidth);
    if (el.scrollLeft > maxScroll) {
      el.scrollLeft = maxScroll;
    }
    const scrollLeft = el.scrollLeft;
    const trackWidth = track.clientWidth;
    if (scrollWidth <= clientWidth + 1 || trackWidth <= 0) {
      thumb.style.display = "none";
      return;
    }
    thumb.style.display = "block";
    const minThumb = 24;
    const thumbWidth = Math.max(
      minThumb,
      Math.floor((clientWidth / scrollWidth) * trackWidth)
    );
    const maxThumbLeft = Math.max(trackWidth - thumbWidth, 0);
    const thumbLeft =
      maxScroll > 0
        ? Math.min(
            maxThumbLeft,
            Math.max(0, (scrollLeft / maxScroll) * maxThumbLeft)
          )
        : 0;
    thumb.style.width = `${thumbWidth}px`;
    thumb.style.left = `${thumbLeft}px`;
  }, []);

  /** Alinea el strip fijo al scroll container y refresca el thumb (resize / layout). */
  const updateMirrorShellAndThumb = useCallback(() => {
    if (!stickyScroll) return;
    const main = scrollContainerRef.current;
    const shell = mirrorOuterRef.current;
    const mirror = mirrorScrollRef.current;
    if (!main || !shell || !mirror) return;
    const scrollRect = main.getBoundingClientRect();
    const vw =
      typeof window !== "undefined" ? window.innerWidth : scrollRect.width;

    const useBleed =
      stickyMirrorBleed &&
      (stickyMirrorOuterRefProp?.current ?? tableRef.current);

    if (useBleed) {
      const outerEl = stickyMirrorOuterRefProp?.current ?? tableRef.current;
      if (!outerEl) return;
      const outerRect = outerEl.getBoundingClientRect();
      const shellLeft = Math.max(0, outerRect.left);
      const shellWidth = Math.min(
        outerRect.width,
        Math.max(0, vw - shellLeft)
      );
      shell.style.left = `${shellLeft}px`;
      shell.style.width = `${shellWidth}px`;
      const insetLeft = Math.max(0, scrollRect.left - outerRect.left);
      const mirrorW = Math.max(0, scrollRect.width);
      mirror.style.marginLeft = `${insetLeft}px`;
      mirror.style.width = `${mirrorW}px`;
      mirror.style.maxWidth = `${mirrorW}px`;
    } else {
      const left = Math.max(0, scrollRect.left);
      const width = Math.min(scrollRect.width, Math.max(0, vw - left));
      shell.style.left = `${left}px`;
      shell.style.width = `${width}px`;
      mirror.style.marginLeft = "0px";
      mirror.style.width = "100%";
      mirror.style.maxWidth = "";
    }

    mirror.scrollLeft = main.scrollLeft;
    updateCustomScrollbarThumb();
  }, [
    stickyScroll,
    stickyMirrorBleed,
    stickyMirrorOuterRefProp,
    updateCustomScrollbarThumb,
  ]);

  /** Uses live DOM metrics so it stays correct in the same tick as scroll metric updates. */
  const updateViewportMirrorPlacement = useCallback(() => {
    const el = scrollContainerRef.current;
    const tableEl = tableMeasureRef.current;
    if (!el || !tableEl) {
      setViewportMirrorActive((prev) => (prev ? false : prev));
      return;
    }
    const overflow = el.scrollWidth > el.clientWidth + 1;
    if (!stickyScroll || !overflow) {
      setViewportMirrorActive((prev) => (prev ? false : prev));
      return;
    }
    const tableRect = tableEl.getBoundingClientRect();
    const bottomBelowViewport = tableRect.bottom > window.innerHeight;
    setViewportMirrorActive((prev) =>
      prev === bottomBelowViewport ? prev : bottomBelowViewport
    );
  }, [stickyScroll]);

  const updateHorizontalScrollMetrics = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const overflow = el.scrollWidth > el.clientWidth + 1;
    setHasHorizontalOverflow(overflow);
    setMirrorWidth(el.scrollWidth);
  }, []);

  useLayoutEffect(() => {
    updateHorizontalScrollMetrics();
    updateViewportMirrorPlacement();
  }, [
    data,
    columns,
    updateHorizontalScrollMetrics,
    updateViewportMirrorPlacement,
  ]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    let shellRaf = 0;
    const scheduleMirrorShell = () => {
      cancelAnimationFrame(shellRaf);
      shellRaf = requestAnimationFrame(() => {
        updateMirrorShellAndThumb();
      });
    };
    const ro = new ResizeObserver(() => {
      updateHorizontalScrollMetrics();
      updateViewportMirrorPlacement();
      scheduleMirrorShell();
    });
    ro.observe(el);
    const onWindowResize = () => {
      updateHorizontalScrollMetrics();
      updateViewportMirrorPlacement();
      scheduleMirrorShell();
    };
    window.addEventListener("resize", onWindowResize);
    return () => {
      cancelAnimationFrame(shellRaf);
      ro.disconnect();
      window.removeEventListener("resize", onWindowResize);
    };
  }, [
    data,
    updateHorizontalScrollMetrics,
    updateViewportMirrorPlacement,
    updateMirrorShellAndThumb,
  ]);

  useEffect(() => {
    updateViewportMirrorPlacement();
    window.addEventListener("scroll", updateViewportMirrorPlacement, true);
    return () => {
      window.removeEventListener("scroll", updateViewportMirrorPlacement, true);
    };
  }, [updateViewportMirrorPlacement, data, mirrorWidth]);

  /** DOM-only: align fixed mirror shell to scroll container (no setState). */
  useLayoutEffect(() => {
    if (!viewportMirrorActive || !stickyScroll) return;
    updateMirrorShellAndThumb();
  }, [
    viewportMirrorActive,
    stickyScroll,
    mirrorWidth,
    data,
    updateMirrorShellAndThumb,
  ]);

  const handleMainHorizontalScroll = useCallback(
    (e) => {
      if (scrollSyncingRef.current) return;
      const mirror = mirrorScrollRef.current;
      if (!mirror || !viewportMirrorActive) return;
      const left = e.currentTarget.scrollLeft;
      if (mirror.scrollLeft === left) {
        updateCustomScrollbarThumb();
        return;
      }
      scrollSyncingRef.current = true;
      mirror.scrollLeft = left;
      scrollSyncingRef.current = false;
      updateCustomScrollbarThumb();
    },
    [viewportMirrorActive, updateCustomScrollbarThumb]
  );

  const handleMirrorHorizontalScroll = useCallback(
    (e) => {
      if (scrollSyncingRef.current) return;
      const main = scrollContainerRef.current;
      if (!main) return;
      const left = e.currentTarget.scrollLeft;
      if (main.scrollLeft !== left) {
        scrollSyncingRef.current = true;
        main.scrollLeft = left;
        scrollSyncingRef.current = false;
      }
      updateCustomScrollbarThumb();
    },
    [updateCustomScrollbarThumb]
  );

  const handleCustomTrackPointerDown = useCallback(
    (e) => {
      const thumbEl = customThumbRef.current;
      if (
        thumbEl &&
        (e.target === thumbEl || thumbEl.contains(e.target))
      ) {
        return;
      }
      const el = mirrorScrollRef.current;
      const track = customTrackRef.current;
      if (!el || !track) return;
      const thumb = customThumbRef.current;
      if (!thumb) return;
      const rect = track.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const trackWidth = track.clientWidth;
      const thumbWidth = thumb.offsetWidth;
      const maxScroll = el.scrollWidth - el.clientWidth;
      const maxThumbLeft = Math.max(trackWidth - thumbWidth, 0);
      if (maxScroll <= 0) return;
      let newThumbLeft = x - thumbWidth / 2;
      newThumbLeft = Math.max(0, Math.min(maxThumbLeft, newThumbLeft));
      el.scrollLeft =
        maxThumbLeft > 0 ? (newThumbLeft / maxThumbLeft) * maxScroll : 0;
      updateCustomScrollbarThumb();
    },
    [updateCustomScrollbarThumb]
  );

  const handleCustomThumbPointerDown = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      const el = mirrorScrollRef.current;
      const track = customTrackRef.current;
      const thumb = customThumbRef.current;
      if (!el || !track || !thumb) return;
      const startX = e.clientX;
      const startScrollLeft = el.scrollLeft;

      const onMove = (moveEvent) => {
        const dx = moveEvent.clientX - startX;
        const maxScroll = el.scrollWidth - el.clientWidth;
        const trackWidth = track.clientWidth;
        const thumbWidth = thumb.offsetWidth;
        const maxThumbLeft = Math.max(trackWidth - thumbWidth, 0);
        const scrollPerPx =
          maxThumbLeft > 0 && maxScroll > 0 ? maxScroll / maxThumbLeft : 0;
        el.scrollLeft = startScrollLeft + dx * scrollPerPx;
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    []
  );

  useLayoutEffect(() => {
    if (
      !stickyScroll ||
      !hasHorizontalOverflow ||
      !viewportMirrorActive ||
      !scrollContainerRef.current ||
      !mirrorScrollRef.current
    ) {
      return;
    }
    mirrorScrollRef.current.scrollLeft = scrollContainerRef.current.scrollLeft;
    updateCustomScrollbarThumb();
  }, [
    stickyScroll,
    hasHorizontalOverflow,
    viewportMirrorActive,
    mirrorWidth,
    data,
    updateCustomScrollbarThumb,
  ]);

  useEffect(() => {
    if (!stickyScroll || !hasHorizontalOverflow || !viewportMirrorActive) {
      return;
    }
    let raf = 0;
    const bump = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => updateMirrorShellAndThumb());
    };
    const ro = new ResizeObserver(() => bump());
    const m = mirrorScrollRef.current;
    const t = customTrackRef.current;
    if (m) ro.observe(m);
    if (t) ro.observe(t);
    window.addEventListener("resize", bump);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("resize", bump);
    };
  }, [
    stickyScroll,
    hasHorizontalOverflow,
    viewportMirrorActive,
    mirrorWidth,
    data,
    updateMirrorShellAndThumb,
  ]);

  // Effect to handle dropdown detection and table height adjustment
  useEffect(() => {
    const handleDropdownToggle = () => {
      // Check for any open dropdowns in the table
      const dropdowns = tableRef.current?.querySelectorAll(
        '[data-headlessui-state="open"]'
      );
      const menuItems = tableRef.current?.querySelectorAll('[role="menu"]');

      if (dropdowns && dropdowns.length > 0) {
        setIsDropdownOpen(true);

        // Find the last row with an open dropdown
        const lastRowWithDropdown = Array.from(dropdowns).reduce(
          (lastRow, dropdown) => {
            const row = dropdown.closest("tr");
            return row &&
              (!lastRow ||
                row.compareDocumentPosition(lastRow) &
                  Node.DOCUMENT_POSITION_FOLLOWING)
              ? row
              : lastRow;
          },
          null
        );

        if (lastRowWithDropdown) {
          const rowRect = lastRowWithDropdown.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const spaceBelow = viewportHeight - rowRect.bottom;

          // Check if dropdown is actually visible and get its height
          const dropdown = lastRowWithDropdown.querySelector('[role="menu"]');
          const dropdownHeight = dropdown
            ? dropdown.getBoundingClientRect().height
            : 300;

          // If there's less space than needed for the dropdown, increase table height
          if (spaceBelow < dropdownHeight) {
            const additionalHeight = dropdownHeight - spaceBelow + 20; // Add 20px buffer
            setTableHeight(`calc(100% + ${additionalHeight}px)`);

            // Scroll to ensure the dropdown is visible
            setTimeout(() => {
              if (dropdown) {
                dropdown.scrollIntoView({
                  behavior: "smooth",
                  block: "end",
                  inline: "nearest",
                });
              }
            }, 100);
          } else {
            // Reset height if there's enough space
            setTableHeight("auto");
          }
        }
      } else {
        setIsDropdownOpen(false);
        setTableHeight("auto");
      }
    };

    // Use MutationObserver to detect dropdown state changes
    if (tableRef.current) {
      dropdownObserverRef.current = new MutationObserver(handleDropdownToggle);
      dropdownObserverRef.current.observe(tableRef.current, {
        attributes: true,
        subtree: true,
        attributeFilter: ["data-headlessui-state"],
      });
    }

    // Also listen for click events to detect dropdown toggles
    const handleClick = (e) => {
      if (
        e.target.closest('[role="menu"]') ||
        e.target.closest("[data-headlessui-state]")
      ) {
        setTimeout(handleDropdownToggle, 50); // Small delay to allow state to update
      }
    };

    // Handle window resize to recalculate positioning
    const handleResize = () => {
      if (isDropdownOpen) {
        setTimeout(handleDropdownToggle, 100);
      }
    };

    document.addEventListener("click", handleClick);
    window.addEventListener("resize", handleResize);

    return () => {
      if (dropdownObserverRef.current) {
        dropdownObserverRef.current.disconnect();
      }
      document.removeEventListener("click", handleClick);
      window.removeEventListener("resize", handleResize);
    };
  }, [data]);

  const getSortIcon = (column) => {
    if (!column.sortable) return null;
    const config = sortConfig?.[column.key];
    if (!config?.direction) {
      return (
        <span className="inline-block text-gray-400 group-hover:text-indigo-600">
          <svg
            className="ml-1 h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
            />
          </svg>
        </span>
      );
    }
    return config.direction === "asc" ? (
      <ChevronUpIcon className="ml-1 h-4 w-4 text-indigo-600" />
    ) : (
      <ChevronDownIcon className="ml-1 h-4 w-4 text-indigo-600" />
    );
  };

  return (
    <div
      ref={tableRef}
      className="w-full min-w-0"
      style={{ height: tableHeight, transition: "height 0.3s ease-in-out" }}
    >
      <div
        className={`w-full min-w-0 py-2 align-middle ${edgeToEdge ? "px-0" : "sm:px-6"} ${className}`}
      >
        <div className="w-full min-w-0">
          {data && data.length > 0 ? (
            <div
              className={`flex w-full min-w-0 max-w-full flex-col rounded-sm ${scrollWrapperClassName}`}
              role="region"
              aria-label="Scrollable table"
            >
            <div
              ref={scrollContainerRef}
              onScroll={handleMainHorizontalScroll}
              className={`w-full min-w-0 overflow-y-visible overscroll-x-contain [-webkit-overflow-scrolling:touch] pb-1 ${
                stickyScroll &&
                hasHorizontalOverflow &&
                viewportMirrorActive
                  ? "overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                  : "overflow-x-auto"
              }`}
            >
            <table
              ref={tableMeasureRef}
              className={`min-w-0 divide-y ${tableDivideClassName} ${
                fixedColumnWidths
                  ? "w-max min-w-full table-fixed"
                  : "min-w-full w-max max-w-none table-auto"
              }`}
            >
              <thead className={theadClassName}>
                <tr>
                  {columns.map((column, colIndex) => (
                    <th
                      key={column.key}
                      scope="col"
                      onClick={() =>
                        column.sortable && onSort ? onSort(column.key) : undefined
                      }
                      className={`text-start text-sm font-semibold text-gray-900 ${
                        getHeaderCellPaddingClass
                          ? `${getHeaderCellPaddingClass(colIndex, columns.length)} leading-5`
                          : "px-3 py-3.5"
                      } ${
                        column.sortable ? "cursor-pointer select-none group" : ""
                      } ${column.headerClassName || ""}`}
                      style={{
                        width: column.width ? column.width : "auto",
                        ...(column.minWidth ? { minWidth: column.minWidth } : {}),
                        // Avoid % maxWidth on th — it squishes single-line header labels in narrow viewports
                        whiteSpace: "nowrap",
                      }}
                    >
                      <div
                        className={`flex items-center ${
                          column.align === "right" ? "justify-end" : ""
                        }`}
                      >
                        <span>{column.header}</span>
                        {getSortIcon(column)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className={tbodyClassName}>
                {data.map((item, index) => {
                  const rowClassName = getRowClassName
                    ? getRowClassName(item, index)
                    : "";
                  const trBase = onRowClick
                    ? "cursor-pointer hover:bg-gray-50"
                    : "";
                  const trClass = useDividedBodyRows
                    ? `transition-colors ${trBase} ${tableRowClassName}`
                    : `border-b border-gray-200 ${trBase} ${tableRowClassName}`;
                  return (
                  <tr
                    key={item[rowKey] ?? item.id ?? index}
                    className={trClass}
                    onClick={onRowClick ? (e) => onRowClick(item, e) : undefined}
                  >
                    {columns.map((column, colIndex) => (
                      <td
                        key={column.key}
                        className={`min-w-0 text-sm align-top break-words [overflow-wrap:anywhere] ${
                          getBodyCellPaddingClass
                            ? getBodyCellPaddingClass(colIndex, columns.length)
                            : "px-3 py-4"
                        } ${
                          column.align === "right" ? "text-right" : ""
                        } ${column.cellClassName || "text-gray-500"} ${
                          resolveCellRowClassName(column, rowClassName)
                        }`}
                        style={
                          fixedColumnWidths && column.width
                            ? {
                                width: column.width,
                                minWidth: column.minWidth,
                                maxWidth: column.contentMaxWidth ?? column.width,
                              }
                            : undefined
                        }
                        onClick={
                          column.onClick
                            ? () => column.onClick(item)
                            : undefined
                        }
                      >
                        {column.render ? column.render(item) : item[column.key]}
                      </td>
                    ))}
                  </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
            {stickyScroll &&
              hasHorizontalOverflow &&
              mirrorWidth > 0 &&
              viewportMirrorActive && (
                <div
                  ref={mirrorOuterRef}
                  className="fixed bottom-0 z-30 box-border min-w-0 max-w-[100vw] rounded-t-lg border-x border-t border-gray-200/90 bg-white pb-2 shadow-[0_-2px_10px_-2px_rgba(15,23,42,0.05),0_-1px_4px_-1px_rgba(15,23,42,0.04)]"
                  role="presentation"
                  aria-hidden
                >
                  <div
                    ref={mirrorScrollRef}
                    onScroll={handleMirrorHorizontalScroll}
                    className="min-w-0 overflow-x-auto overflow-y-hidden overscroll-x-contain [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                  >
                    <div
                      className="h-1"
                      style={{ width: mirrorWidth, minWidth: "100%" }}
                    />
                  </div>
                  <div
                    ref={customTrackRef}
                    className="relative mt-1 h-2.5 w-full min-w-0 cursor-pointer select-none overflow-hidden rounded-full"
                    onPointerDown={handleCustomTrackPointerDown}
                  >
                    <div
                      className="pointer-events-none absolute inset-0 rounded-full bg-[#eceaf3]"
                      aria-hidden
                    />
                    <div
                      ref={customThumbRef}
                      className="absolute top-0 h-2.5 min-w-[24px] touch-none cursor-grab rounded-full bg-[#c5c4c8] hover:bg-[#bab9bf] active:cursor-grabbing"
                      style={{ left: 0 }}
                      onPointerDown={handleCustomThumbPointerDown}
                      aria-hidden
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center mt-10">
              <span className="p-3 rounded-full bg-custom-link-bg-color flex justify-center items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  style={{ color: "white" }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </span>
              <p className="mt-3 text-lg text-gray-700 font-semibold">
                {emptyMessage}
              </p>
              <p className="text-gray-500 mt-1 text-center">
                {isSearchActive
                  ? "Your search did not match any records. Please try again."
                  : emptyDescription}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomTable;
